import type { InstallableModel } from '../types/LocalAiTypes';
import { LOCAL_AI_CACHE, isAssetCached } from './ModelCache';

export interface ModelDownloadProgress {
  modelId: string;
  completedBytes: number;
  totalBytes: number;
  progress: number;
}

export async function downloadModels(
  models: readonly InstallableModel[],
  onProgress?: (progress: ModelDownloadProgress) => void,
  signal?: AbortSignal,
): Promise<void> {
  if (!('caches' in globalThis)) throw new Error('This browser does not provide the Cache API required for local models.');
  const totalBytes = models.reduce((total, model) => total + model.totalBytes, 0);
  let completedBytes = 0;
  const cache = await caches.open(LOCAL_AI_CACHE);
  for (const model of models) {
    for (const asset of model.assets) {
      if (signal?.aborted) throw new DOMException('Model installation was cancelled.', 'AbortError');
      if (await isAssetCached(asset)) {
        completedBytes += asset.bytes;
        onProgress?.({ modelId: model.id, completedBytes, totalBytes, progress: completedBytes / totalBytes });
        continue;
      }
      const request = new Request(asset.url, {
        credentials: 'include',
        cache: 'no-store',
        ...(signal === undefined ? {} : { signal }),
      });
      const response = await fetch(request);
      if (!response.ok) throw new Error(`Varza could not download ${model.displayName} (${response.status}).`);
      const checksum = response.headers.get('X-Content-SHA256');
      if (checksum !== asset.sha256) throw new Error(`The server returned an unexpected checksum for ${asset.path}.`);
      await cache.put(asset.url, response);
      completedBytes += asset.bytes;
      onProgress?.({ modelId: model.id, completedBytes, totalBytes, progress: completedBytes / totalBytes });
    }
  }
}
